import _ from 'lodash';
import type { z } from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType, type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { mergeMissedMarks } from './utils';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { DBLegacyExamAttemptSchema, LegacyExamAttemptSchema } from './schemas';

type DBLegacyExamAttempt = z.infer<typeof DBLegacyExamAttemptSchema>;

export const LegacyExamAttemptCodecConfig: TransformerConfig<
  typeof LegacyExamAttemptSchema,
  typeof DBLegacyExamAttemptSchema,
  'id' | 'examId'
> = {
  name: 'LegacyExamAttempt',
  clientSchema: LegacyExamAttemptSchema,
  dbSchema: DBLegacyExamAttemptSchema,
  transformers: [
    createRenameTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>(
      'studentId',
      'student'
    ),
    // subject, examCode, attemptNumber ↔ subjectExamQuestionIdentifier, exam, question
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      encoder: {
        sourceFields: ['subject', 'examCode', 'attemptNumber'] as const,
        encode: ({ subject, examCode, attemptNumber }) => ({
          exam: `${subject}#${examCode}`,
          subjectExamQuestionIdentifier: `${LegacyExamAttemptType.LegacyExam}#${subject}#${examCode}#${attemptNumber}`,
          // Keeps an exam attempt apart from the question attempts on the exam's index
          question: '_' as const,
        }),
      },
      decoder: {
        sourceFields: ['subjectExamQuestionIdentifier', 'exam', 'question'] as const,
        decode: ({ subjectExamQuestionIdentifier }) => {
          if (subjectExamQuestionIdentifier === undefined) {
            return {};
          }
          const parts = subjectExamQuestionIdentifier.split('#');
          return {
            subject: parts[1] as Subject,
            examCode: parts[2] as string,
            attemptNumber: _.toInteger(parts[3]),
          };
        },
      },
    }),
    // status: LegacyExamAttemptStatus ↔ status: 'EXAM#status'
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      encoder: {
        sourceFields: ['status'] as const,
        encode: ({ status }) => ({
          status: `${LegacyExamAttemptType.LegacyExam}#${status}`,
        }),
      },
      decoder: {
        sourceFields: ['status'] as const,
        decode: ({ status }) => ({
          status: (status as DBLegacyExamAttempt['status']).split(
            '#'
          )[1] as LegacyExamAttemptStatus,
        }),
      },
    }),
    // Merge missedMarks into inaccuracyMarks for each analytics sub-object on decode
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExamAttempt['analytics'];
          return {
            analytics: Object.fromEntries(
              Object.entries(dbAnalytics).map(([key, value]) => [key, mergeMissedMarks(value)])
            ),
          };
        },
      },
    }),
  ],
  derived: {
    id: attempt => buildRecordId('legacyExamAttempt', attempt),
    examId: attempt =>
      buildRecordId('legacyExam', { subject: attempt.subject, code: attempt.examCode }),
  },
};

export const encodeLegacyExamAttemptToDb = createEncodeToDbFn(LegacyExamAttemptCodecConfig);
export const decodeLegacyExamAttemptFromDb = createDecodeFromDbFn(LegacyExamAttemptCodecConfig);
